// Calendar-date arithmetic on `YYYY-MM-DD` strings. "Today" is the device's
// local date. Strings are compared directly; nothing goes through UTC except
// the day-difference sum, which uses UTC components precisely so that clock
// changes cannot shift it.

import type { DateStr, TimeStr } from '@/types'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

export const pad2 = (n: number): string => String(n).padStart(2, '0')

export function isDateStr(s: unknown): s is DateStr {
  return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)
}

export function parts(s: DateStr): { y: number; m: number; d: number } {
  const [y, m, d] = s.split('-').map(Number)
  return { y, m, d }
}

export function toDateStr(y: number, m: number, d: number): DateStr {
  return `${y}-${pad2(m)}-${pad2(d)}`
}

/** The local calendar date of a moment. */
export function dateOf(date: Date): DateStr {
  return toDateStr(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

/** The local clock time of a moment, `HH:mm`. */
export function timeOf(date: Date): TimeStr {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

export function today(now: Date = new Date()): DateStr {
  return dateOf(now)
}

/** Local midnight of a calendar date. */
export function dateFrom(s: DateStr): Date {
  const { y, m, d } = parts(s)
  return new Date(y, m - 1, d)
}

/** A local moment from a calendar date and a clock time. */
export function dateTimeFrom(s: DateStr, t: TimeStr): Date {
  const { y, m, d } = parts(s)
  const [hh, mm] = t.split(':').map(Number)
  return new Date(y, m - 1, d, hh, mm)
}

export function addDays(s: DateStr, n: number): DateStr {
  const { y, m, d } = parts(s)
  const next = new Date(Date.UTC(y, m - 1, d + n))
  return toDateStr(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate())
}

/** Days from `a` to `b`: positive when `b` is later. */
export function dayDiff(a: DateStr, b: DateStr): number {
  const pa = parts(a)
  const pb = parts(b)
  return Math.round((Date.UTC(pb.y, pb.m - 1, pb.d) - Date.UTC(pa.y, pa.m - 1, pa.d)) / 864e5)
}

export function lastDayOfMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate()
}

/** Adds months, keeping `monthDay` and clamping to the month's last day: 31 Jan, 28 Feb, 31 Mar. */
export function addMonthsClamped(s: DateStr, n: number, monthDay?: number | null): DateStr {
  const { y, m, d } = parts(s)
  const want = monthDay ?? d
  const total = y * 12 + (m - 1) + n
  const ny = Math.floor(total / 12)
  const nm = (total % 12) + 1
  return toDateStr(ny, nm, Math.min(want, lastDayOfMonth(ny, nm)))
}

export function addYearsClamped(s: DateStr, n: number): DateStr {
  const { y, m, d } = parts(s)
  return toDateStr(y + n, m, Math.min(d, lastDayOfMonth(y + n, m)))
}

/** ISO weekday: 1 = Monday to 7 = Sunday. */
export function isoWeekday(s: DateStr): number {
  const { y, m, d } = parts(s)
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return wd === 0 ? 7 : wd
}

/** The Monday on or before the date. Weeks start on Monday. */
export function startOfWeek(s: DateStr): DateStr {
  return addDays(s, 1 - isoWeekday(s))
}

export function endOfWeek(s: DateStr): DateStr {
  return addDays(startOfWeek(s), 6)
}

export function startOfMonth(s: DateStr): DateStr {
  const { y, m } = parts(s)
  return toDateStr(y, m, 1)
}

export function endOfMonth(s: DateStr): DateStr {
  const { y, m } = parts(s)
  return toDateStr(y, m, lastDayOfMonth(y, m))
}

export function weekdayName(s: DateStr): string {
  const { y, m, d } = parts(s)
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]
}

export function monthName(m: number, long = false): string {
  return (long ? MONTHS_LONG : MONTHS)[m - 1]
}

/** "Tue 6 Oct"; with `withYear`, "Tue 6 Oct 2026". */
export function fmtDate(s: DateStr, withYear = false): string {
  const { y, m, d } = parts(s)
  return `${weekdayName(s)} ${d} ${monthName(m)}${withYear ? ` ${y}` : ''}`
}

/** "Tue 6 Oct", or "Today" when the date is today. */
export function fmtDay(s: DateStr, todayStr: DateStr): string {
  return s === todayStr ? 'Today' : fmtDate(s)
}

/** "October 2026" */
export function fmtMonthYear(s: DateStr): string {
  const { y, m } = parts(s)
  return `${monthName(m, true)} ${y}`
}

/** "5 – 11 Oct", "28 Sep – 4 Oct", or with years when the week crosses one. */
export function fmtWeekSpan(start: DateStr, end: DateStr): string {
  const a = parts(start)
  const b = parts(end)
  if (a.y !== b.y) return `${a.d} ${monthName(a.m)} ${a.y} – ${b.d} ${monthName(b.m)} ${b.y}`
  if (a.m !== b.m) return `${a.d} ${monthName(a.m)} – ${b.d} ${monthName(b.m)}`
  return `${a.d} – ${b.d} ${monthName(a.m)}`
}

/**
 * A date range: "Sat 10 – Mon 12 Oct" within a month, "Sat 31 Oct – Mon 2 Nov"
 * across months, and with the years when it crosses a year.
 */
export function fmtRange(start: DateStr, end: DateStr): string {
  const a = parts(start)
  const b = parts(end)
  if (a.y !== b.y) return `${fmtDate(start, true)} – ${fmtDate(end, true)}`
  if (a.m !== b.m) return `${fmtDate(start)} – ${fmtDate(end)}`
  return `${weekdayName(start)} ${a.d} – ${fmtDate(end)}`
}

/** "Tue 6 Oct 2026" for the footer, and relative words for today and yesterday. */
export function fmtDateLong(s: DateStr, todayStr: DateStr): string {
  if (s === todayStr) return 'today'
  if (s === addDays(todayStr, -1)) return 'yesterday'
  const { y } = parts(s)
  const thisYear = parts(todayStr).y
  return fmtDate(s, y !== thisYear)
}

export function isTimeStr(s: unknown): s is TimeStr {
  return typeof s === 'string' && /^\d{2}:\d{2}$/.test(s)
}

export function minutesOf(t: TimeStr): number {
  const [h, m] = t.split(':').map(Number)
  return h * 60 + m
}

export function timeFromMinutes(total: number): TimeStr {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, total))
  return `${pad2(Math.floor(clamped / 60))}:${pad2(clamped % 60)}`
}
